import React from 'react'
import { useNavigate } from 'react-router-dom'
import { FileText, Brain, Shield, Zap, ArrowRight, MessageSquare, Sparkles, Target } from 'lucide-react'

const WelcomePage: React.FC = () => {
  const navigate = useNavigate()

  const features = [
    {
      icon: <FileText className="w-8 h-8 text-blue-600" />,
      title: 'Full Word Compatibility',
      description: 'Create, edit, and format documents with complete Microsoft Word feature parity'
    },
    {
      icon: <Brain className="w-8 h-8 text-green-600" />,
      title: 'Intelligent AI Assistant',
      description: 'Dynamic conversation-based document creation with context-aware questioning'
    },
    {
      icon: <MessageSquare className="w-8 h-8 text-purple-600" />,
      title: 'Natural Language Interface',
      description: 'Describe what you need in plain English - no rigid templates or checkboxes'
    },
    {
      icon: <Sparkles className="w-8 h-8 text-orange-600" />,
      title: 'Smart Document Generation',
      description: 'AI creates documents from scratch based on your specific requirements'
    }
  ]

  const aiCapabilities = [
    {
      icon: <Target className="w-6 h-6 text-blue-600" />,
      title: 'Intent Recognition',
      description: 'AI understands your goals and adapts its approach accordingly'
    },
    {
      icon: <MessageSquare className="w-6 h-6 text-green-600" />,
      title: 'Dynamic Questioning',
      description: 'Intelligent follow-up questions that adapt based on your responses'
    },
    {
      icon: <Brain className="w-6 h-6 text-purple-600" />,
      title: 'Context Awareness',
      description: 'Remembers your conversation and builds upon previous information'
    },
    {
      icon: <Zap className="w-6 h-6 text-orange-600" />,
      title: 'Natural Generation',
      description: 'Creates documents that feel naturally written, not template-filled'
    }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto px-4 py-16">
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            Legal Word Editor
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            The intelligent AI-powered document editor designed specifically for legal professionals. 
            Create documents through natural conversation, not rigid templates.
          </p>
          <button
            onClick={() => navigate('/editor')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg text-lg font-semibold flex items-center gap-2 mx-auto transition-colors duration-200"
          >
            Start Creating Documents
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {features.map((feature, index) => (
            <div key={index} className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200">
              <div className="mb-4">{feature.icon}</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
              <p className="text-gray-600">{feature.description}</p>
            </div>
          ))}
        </div>

        {/* AI Capabilities Section */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Intelligent AI Capabilities
          </h2>
          <p className="text-gray-600 text-center mb-8 max-w-2xl mx-auto">
            Our AI doesn't use rigid templates or fixed question sets. Instead, it engages in intelligent conversation 
            to understand your needs and create documents tailored to your specific situation.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {aiCapabilities.map((capability, index) => (
              <div key={index} className="text-center p-4">
                <div className="mb-3 flex justify-center">{capability.icon}</div>
                <h3 className="font-semibold text-gray-900 mb-2">{capability.title}</h3>
                <p className="text-sm text-gray-600">{capability.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* How It Works Section */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg shadow-lg p-8 mb-16 text-white">
          <h2 className="text-2xl font-bold mb-6 text-center">
            How It Works
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-3xl font-bold mb-2">1</div>
              <h3 className="font-semibold mb-2">Describe Your Need</h3>
              <p className="text-blue-100">Tell the AI what you need in your own words</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold mb-2">2</div>
              <h3 className="font-semibold mb-2">Intelligent Conversation</h3>
              <p className="text-blue-100">AI asks contextual questions to gather information</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold mb-2">3</div>
              <h3 className="font-semibold mb-2">Document Generated</h3>
              <p className="text-blue-100">AI creates a tailored document from scratch</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Why Choose Legal Word Editor?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600 mb-2">100%</div>
              <div className="text-gray-600">Word Compatibility</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600 mb-2">10x</div>
              <div className="text-gray-600">Faster Document Creation</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600 mb-2">Intelligent</div>
              <div className="text-gray-600">AI Conversation</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default WelcomePage 